import { DecimalPipe, Location, NgStyle } from '@angular/common';
import { Component, OnDestroy, effect, inject, signal, untracked, viewChild } from '@angular/core';
import { ActivatedRoute, Router, RouterLink } from '@angular/router';
import { CartStore } from '@apps/mtjr/customers/app/cart/cart.store';
import {
  IonBackButton,
  IonButton,
  IonButtons,
  IonContent,
  IonFooter,
  IonHeader,
  IonIcon,
  IonItem,
  IonLabel,
  IonModal,
  IonText,
  IonTitle,
  IonToolbar,
  ModalController,
} from '@ionic/angular/standalone';
import { VxErrorComponent } from '@libs/core/lib/error-view/error-view.component';
import { NgEventBus } from '@libs/core/lib/event-bus/ng-event-bus';
import { VxCurrencyPipe } from '@libs/core/lib/pipes/mtjr-currency.pipe';
import { VxLoadingComponent } from '@libs/core/lib/shared/loading-state';
import { VxImageComponent } from '@libs/core/lib/shared/mtjr-image.component';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';

import { VxErrorBarComponent } from '@libs/core/lib/error-view/error-bar.component';
import { VxSubmitButtonComponent } from '@libs/core/lib/form-controls/mtjr-submit-button.component';

import { BASE_CONFIG } from '@libs/core/base-config';
import { QuarterFloorPipe } from '@libs/core/lib/pipes/quarter-floor.pipe';
import { AppAlerts } from '@libs/core/lib/utils/app-alerts';
import { withBuyNow } from '@libs/mtjr/shared/generated/entities/order/order-store-features';
import { withRelatedProductsList } from '@libs/mtjr/shared/generated/entities/product/product-store-features';
import { Dtos, Entity } from '@libs/mtjr/shared/generated/types';
import { NotificationsStore } from '@libs/mtjr/shared/notifications/notifications.store';
import { HeartButtonComponent } from '@libs/mtjr/shared/products/heart_button.component';
import { VxProductCardComponent } from '@libs/mtjr/shared/products/product_card.component';
import { VxProductAttributesSectionComponent } from '@libs/mtjr/shared/products/product_details/mtjr-product-attributes-section.component';
import { ProductDetailsStore } from '@libs/mtjr/shared/products/product_details/product-details.store';
import { VxProductSwiperComponent } from '@libs/mtjr/shared/products/product_details/product_swiper.component';
import { VxProductReviewsSectionComponent } from '@libs/mtjr/shared/products/product_reviews/mtjr-product-reviews-section.component';
import { WalletStore } from '@libs/mtjr/shared/wallet/wallet.store';
import { signalStore } from '@ngrx/signals';
import { once } from 'lodash-es';
import { v4 } from 'uuid';

const RelatedProductsStore = signalStore(withRelatedProductsList());

const BuyNowStore = signalStore(withBuyNow());

@Component({
  selector: 'mac-product-details',
  providers: [ProductDetailsStore, RelatedProductsStore, BuyNowStore],
  imports: [
    IonFooter,
    IonItem,
    IonTitle,
    IonText,
    IonHeader,
    IonToolbar,
    IonButton,
    IonButtons,
    RouterLink,
    IonIcon,
    IonBackButton,
    IonContent,
    VxCurrencyPipe,
    NgStyle,
    VxImageComponent,
    VxLoadingComponent,
    VxErrorComponent,
    DecimalPipe,
    VxProductCardComponent,
    HeartButtonComponent,
    TranslatePipe,
    IonModal,
    VxSubmitButtonComponent,
    VxErrorBarComponent,
    QuarterFloorPipe,
    IonLabel,
    VxProductSwiperComponent,
    VxProductAttributesSectionComponent,
    VxProductReviewsSectionComponent,
  ],
  templateUrl: './product-details.page.html',
})
export class ProductDetailsPage implements OnDestroy {
  route = inject(ActivatedRoute);
  store = inject(ProductDetailsStore);
  relatedProductsStore = inject(RelatedProductsStore);
  buyNowStore = inject(BuyNowStore);
  alerts = inject(AppAlerts);
  cartStore = inject(CartStore);
  vxSwiper = viewChild(VxProductSwiperComponent);
  productId = this.route.snapshot.params['productId'] as string;
  notificationsStore = inject(NotificationsStore);
  eventBus = inject(NgEventBus);
  translate = inject(TranslateService);
  router = inject(Router);
  location = inject(Location);
  buyNowModalArgs = signal<{ product: Dtos.Product; variation: Dtos.ProductVariation } | undefined>(undefined);
  walletStore = inject(WalletStore);
  modalCtrl = inject(ModalController);
  idempotencyKey = signal(v4());
  shareModalOpen = signal(false);
  currentShareUrl = signal('');
  private readonly baseConfig = inject(BASE_CONFIG);
  shareBaseUrl = this.baseConfig.shareBaseUrl;

  constructor() {
    this.store.getProductById({ id: this.productId });
    const relatedProductsOnce = once(() => {
      this.relatedProductsStore.relatedProductsListPage({ productId: this.productId });
    });
    effect(() => {
      if (this.store.getProductByIdState().success) {
        console.log('Product details loaded');
        untracked(() => {
          relatedProductsOnce();
        });
      }
    });

    this.eventBus.on<{ productId: string }>('product:fetch').subscribe((data) => {
      if (data.data?.productId === this.productId) {
        this.store.getProductById({ id: this.productId });
      }
    });

    effect(() => {
      const variant = this.store.selectedVariation();
      const swiper = this.vxSwiper();
      if (variant?.image && swiper) {
        const index = this.store.dynamicInfo().images.findIndex((x) => x === variant.image);
        if (index !== -1) {
          swiper.slideTo(index);
        }
      }
    });

    effect(async () => {
      const state = this.buyNowStore.buyNowState();
      if (state.success) {
        this.buyNowModalArgs.set(undefined);
        this.idempotencyKey.set(v4());
        this.alerts.successSnackbarTR('PRODUCTS.BUY_NOW_INITIATED');

        this.router.navigate(['/orders', state.data.id]);
      } else if (state.type === 'error' && state.failure.statusCode !== 0) {
        this.idempotencyKey.set(v4());
      }
    });
  }

  onAttributeToggle(attrValue: Entity.VariationAttribute) {
    this.store.toggleAttribute(attrValue);
  }

  addToCart() {
    const product = this.store.product();
    const variation = this.store.selectedVariation();
    if (product) {
      this.cartStore.addItem({
        product,
        variation,
        quantity: 1,
      });
      this.alerts.successSnackbarTR('PRODUCTS.ADDED_TO_CART', {
        positionAnchor: 'footer',
        position: 'bottom',
        duration: 3000,
        buttons: [
          {
            text: this.translate.instant('COMMON.CART'),
            handler: () => {
              this.router.navigate(['/cart']);
              return true;
            },
            icon: 'cart',
          },
        ],
      });
    } else {
      throw new Error('Product or variation not found');
    }
  }

  async buyNow() {
    const product = this.store.product();
    const variation = this.store.selectedVariation();
    if (!product || !variation) {
      throw new Error('Product or variation not found');
    }

    this.buyNowModalArgs.set({ product, variation });
  }

  async confirmBuyNow(args: { product: Dtos.Product; variation: Dtos.ProductVariation }) {
    if (!this.notificationsStore.isGranted()) {
      await this.notificationsStore.requestPermission({
        showAlert: true,
        trMessage: 'NOTIFICATIONS.ALLOW_NOTIFICATIONS_FOR_PURCHASES',
      });
    }

    await this.buyNowStore.buyNow({
      productId: args.product.id,
      variationId: args.variation.id,
      finalPrice: args.variation.price,
      paymentMethod: 'wallet',
      idempotencyKey: this.idempotencyKey(),
    });
  }

  topUpWallet() {
    this.buyNowModalArgs.set(undefined);
    this.router.navigate(['/', 'wallet'], { queryParams: { action: 'top-up' } });
  }

  ngOnDestroy() {
    this.modalCtrl.dismiss();
  }

  async openShareOptions(productId: string) {
    const shareUrl = `${this.shareBaseUrl}/p/${productId}`;
    this.currentShareUrl.set(shareUrl);
    this.openShareModal();
  }

  openShareModal() {
    this.shareModalOpen.set(true);
  }

  copyToClipboard(text: string) {
    navigator.clipboard
      .writeText(text)
      .then(() => {
        this.alerts.successSnackbarTR('COMMON.LINK_COPIED', { duration: 2000 });
      })
      .catch(() => {
        this.alerts.snackbarTR('COMMON.COPY_FAILED', { duration: 2000 });
      });
  }

  async shareToSocialMedia(platform: string, url: string) {
    // Input validation
    if (!platform || !url) {
      this.alerts.snackbarTR('COMMON.INVALID_SHARE_PARAMETERS');
      return;
    }

    // Validate URL format
    try {
      new URL(url);
    } catch {
      this.alerts.snackbarTR('COMMON.INVALID_URL');
      return;
    }

    const product = this.store.product();
    if (!product) {
      this.alerts.snackbarTR('COMMON.PRODUCT_NOT_FOUND');
      return;
    }

    // Check if Web Share API is available (mobile optimization)
    if (navigator.share && platform === 'native') {
      try {
        await navigator.share({
          title: product.name,
          text: product.description ?? undefined,
          url: url,
        });
        return;
      } catch (error) {
        // User cancelled or error occurred, fall back to URL sharing
        console.warn('Web Share API failed:', error);
      }
      return;
    }

    const encodedUrl = encodeURIComponent(url);
    const title = encodeURIComponent(product.name || '');
    const description = encodeURIComponent(product.description || '');

    let shareUrl = '';

    switch (platform) {
      case 'facebook':
        shareUrl = `https://www.facebook.com/sharer/sharer.php?u=${encodedUrl}`;
        break;
      case 'twitter': {
        // X/Twitter - properly format text with spacing
        const tweetText = description ? `${title} - ${description}` : title;
        shareUrl = `https://twitter.com/intent/tweet?url=${encodedUrl}&text=${encodeURIComponent(tweetText)}`;
        break;
      }
      case 'whatsapp':
        shareUrl = `https://wa.me/?text=${encodeURIComponent(`${product.name} ${url}`)}`;
        break;
      case 'telegram':
        shareUrl = `https://t.me/share/url?url=${encodedUrl}&text=${title}`;
        break;
      case 'linkedin':
        shareUrl = `https://www.linkedin.com/sharing/share-offsite/?url=${encodedUrl}`;
        break;
      default:
        this.alerts.snackbarTR('COMMON.UNSUPPORTED_PLATFORM');
        return;
    }

    if (shareUrl) {
      try {
        const newWindow = window.open(shareUrl, '_blank', 'noopener,noreferrer');
        if (!newWindow) {
          // Popup blocked - provide fallback
          this.copyToClipboard(url);
          this.alerts.snackbarTR('COMMON.POPUP_BLOCKED_LINK_COPIED');
        }
      } catch (error) {
        console.error('Failed to open share window:', error);
        // Fallback to copying link
        this.copyToClipboard(url);
        this.alerts.snackbarTR('COMMON.SHARE_FAILED_LINK_COPIED');
      }
    }
  }
}
